import type {
  NestedTableLevelQuery,
  NestedTableQueryColumn,
  NestedTableQueryFilter,
  NestedTableRow,
} from './nested-table.types';

const ROW_TOKEN = /^\[([^\]]+)]$/;

/** Resolve only filter values. Formula references such as `[sales_2026]`
 * deliberately remain untouched because the compute service evaluates them. */
export function resolveNestedLevelQuery(
  query: NestedTableLevelQuery,
  contextRows: NestedTableRow[],
): NestedTableLevelQuery {
  return {
    ...query,
    columns: query.columns.map((column) => ({
      ...column,
      ...(column.filters ? { filters: column.filters.map((filter) => resolveFilter(filter, contextRows)) } : {}),
    })),
    ...(query.filters ? { filters: query.filters.map((filter) => resolveFilter(filter, contextRows)) } : {}),
    ...(query.sorting ? { sorting: query.sorting.map((sort) => ({ ...sort })) } : {}),
    ...(query.pagination ? { pagination: { ...query.pagination } } : {}),
  };
}

/** The compute API normally returns displayName keys. Duplicate those values
 * under their source field too so existing renderer configs and tokens work. */
export function normalizeNestedQueryRows(
  rows: NestedTableRow[],
  columns: NestedTableQueryColumn[],
): NestedTableRow[] {
  return rows.map((row) => {
    const copy = { ...row };
    columns.forEach((column) => {
      const field = String(column.field ?? '').trim();
      const alias = String(column.displayName ?? '').trim();
      if (!field || !alias || field === alias) return;
      if (copy[field] === undefined && copy[alias] !== undefined) copy[field] = copy[alias];
      if (copy[alias] === undefined && copy[field] !== undefined) copy[alias] = copy[field];
    });
    return copy;
  });
}

export function nestedQueryRows(response: unknown, rowsKey = ''): NestedTableRow[] {
  if (Array.isArray(response)) return response as NestedTableRow[];
  const outer = (response ?? {}) as Record<string, unknown>;
  const result = isRecord(outer['result']) ? outer['result'] : outer;
  const data = result['data'] ?? outer['data'];
  if (rowsKey) {
    const keyed = readPath(result, rowsKey) ?? readPath(outer, rowsKey);
    if (Array.isArray(keyed)) return keyed as NestedTableRow[];
  }
  if (Array.isArray(data)) return data as NestedTableRow[];
  if (isRecord(data)) {
    const nested = data[rowsKey || 'tableRows'] ?? data['rows'];
    if (Array.isArray(nested)) return nested as NestedTableRow[];
  }
  const fallback = result[rowsKey || 'tableRows'] ?? result['rows'];
  return Array.isArray(fallback) ? (fallback as NestedTableRow[]) : [];
}

function resolveFilter(filter: NestedTableQueryFilter, contextRows: NestedTableRow[]): NestedTableQueryFilter {
  const match = typeof filter.value === 'string' ? filter.value.trim().match(ROW_TOKEN) : null;
  if (!match) return { ...filter };
  const key = match[1].trim();
  for (let index = contextRows.length - 1; index >= 0; index -= 1) {
    const row = contextRows[index];
    if (Object.prototype.hasOwnProperty.call(row, key)) return { ...filter, value: row[key] };
    const actual = Object.keys(row).find((candidate) => candidate.toLowerCase() === key.toLowerCase());
    if (actual) return { ...filter, value: row[actual] };
  }
  return { ...filter };
}

function readPath(source: Record<string, unknown>, path: string): unknown {
  return path.split('.').filter(Boolean).reduce<unknown>((value, key) => isRecord(value) ? value[key] : undefined, source);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}
